
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Bill, BillStats } from "../types";

export const useTransactionHistory = (schoolId: string) => {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<BillStats[]>([]);
  const [schoolName, setSchoolName] = useState("");

  useEffect(() => {
    const fetchBills = async () => {
      try {
        // Fetch school name
        const { data: clientData } = await supabase
          .from('clients')
          .select('nama_sekolah')
          .eq('school_id', schoolId)
          .single();

        if (clientData) {
          setSchoolName(clientData.nama_sekolah);
        }

        // Fetch bills
        const { data: billsData, error } = await supabase
          .from('bills')
          .select(`
            id,
            transaction_number,
            amount,
            tax,
            discount,
            discount_type,
            status,
            due_date,
            description,
            plan_id,
            plan_price,
            subscription_id,
            user_id,
            created_at,
            plan:plans(
              id,
              name,
              price,
              product:products(name)
            )
          `)
          .eq('school_id', schoolId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        console.log("TransactionHistory raw bills:", billsData);

        // Transform the data to match the Bill interface
        const transformedBills: Bill[] = (billsData || []).map(bill => ({
          id: bill.id,
          amount: bill.amount,
          tax: bill.tax,
          discount: bill.discount,
          description: bill.description,
          status: bill.status as Bill['status'], // Explicitly cast to the union type
          due_date: bill.due_date,
          transaction_number: bill.transaction_number,
          discount_type: bill.discount_type as Bill['discount_type'],
          plan_price: bill.plan_price,
          subscription_id: bill.subscription_id,
          user_id: bill.user_id,
          created_at: bill.created_at,
          plan: {
            name: bill.plan?.name || '',
            price: bill.plan?.price || 0,
            product: {
              name: bill.plan?.product?.name || ''
            }
          }
        }));

        setBills(transformedBills);

        // Calculate stats
        const statsMap = (billsData || []).reduce((acc: { [key: string]: number }, bill) => {
          acc[bill.status] = (acc[bill.status] || 0) + 1;
          return acc;
        }, {});

        const statsArray = Object.entries(statsMap).map(([status, count]) => ({
          status,
          count,
        }));

        setStats(statsArray);
      } catch (error) {
        console.error('Error fetching bills:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBills();
  }, [schoolId]);

  return { bills, loading, stats, schoolName };
};
